import { Scene } from "@babylonjs/core/scene";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { PointerEventTypes, PointerInfo } from "@babylonjs/core/Events/pointerEvents";
import type { PointerInfoPre } from "@babylonjs/core/Events/pointerEvents";
import type { PickingInfo } from "@babylonjs/core/Collisions/pickingInfo";
import type { Camera } from "@babylonjs/core/Cameras/camera";
import type { Observer } from "@babylonjs/core/Misc/observable";
import "@babylonjs/core/Culling/ray";
import "@babylonjs/core/Collisions/collisionCoordinator";
import "@babylonjs/core/Collisions/pickingInfo";
import type { TraceScene } from "../core/TraceScene";
import type { SceneObject } from "../core/SceneObject";
import { COLLIDER_MESH_SUFFIX } from "../behaviors/boxcollider";
import { Logger } from "../utils/Logger";

/** Babylon pick eligibility, shared by ordinary hover and overlap picking. */
export function isPickableMesh(mesh: AbstractMesh): boolean {
    return mesh.isPickable && mesh.isVisible && mesh.isReady() && mesh.isEnabled();
}

export interface ClickToSelectOptions {
    /** Enable nearest-first overlap cycling in edit mode only. Default: false. */
    cycle?: boolean;
    /** Map a hit's owner to a host selection unit; null gates that hit. */
    resolveSelectionUnit?: (raw: SceneObject, hit: PickingInfo) => SceneObject | null;
    /** Ignore host gizmo meshes when picking through to content. */
    isGizmoMesh?: (mesh: AbstractMesh) => boolean;
    /** Include host-owned multi-selection in cycle invalidation. */
    getSelection?: () => readonly SceneObject[];
}

/** The selection targets under the pointer, nearest first, what each hit resolved to, and the view they were picked in. */
type Candidates = { targets: SceneObject[]; hasContent: boolean; shown: Map<AbstractMesh, SceneObject | null>; view: string };

/**
 * Handles pointer input for click-to-select and hover tracking.
 * Forwards events to TraceScene — visual outline is managed separately
 * by {@link SelectionOutline}.
 */
export class ClickToSelect {
    private readonly scene: Scene;
    private readonly traceScene: TraceScene;
    private clickToSelectEnabled: boolean = false;
    private pointerObserver: Observer<PointerInfo> | null = null;
    private prePointerObserver: Observer<PointerInfoPre> | null = null;
    private options: ClickToSelectOptions = {};
    private static readonly CYCLE_THRESHOLD_SQUARED = 4 * 4;
    /** `shown` is what each hit resolved to, nearest first, when the cycle last picked; `view` is the view it picked in. */
    private cycle: { x: number; y: number; nextIndex: number; targets: SceneObject[]; shown: Candidates['shown']; view: string } | null = null;
    private sample: { x: number; y: number; view: string; hits: PickingInfo[] } | null = null;
    private candidates: Candidates | null = null;
    private selection: readonly (SceneObject | null)[] = [];
    private selecting = false;
    private pointerDownTime = 0;
    private lastClick: { time: number; x: number; y: number } | null = null;
    private readonly selectionChanged = () => {
        if (!this.selecting) this._checkSelection();
    };

    // Hover state tracking - stores direct reference to avoid duplicate name issues
    private hoveredObject: SceneObject | null = null;

    // Click vs drag detection
    private pointerDownPosition: { x: number; y: number } | null = null;
    private pointerDownPickInfo: PointerInfo | null = null;
    private static readonly CLICK_THRESHOLD = 5; // pixels - movement beyond this is a drag

    // External drag signal. XR controllers synthesize POINTERDOWN/UP with
    // near-zero screen-space delta, so the 5px threshold can't distinguish
    // a hand grab from a click. Drag sources (XR 6DoF, etc.) call
    // notifyDragOccurred() to suppress the next select fire.
    private _dragOccurred = false;

    // Ctrl/meta/shift as DOM keyboard events last left them, for a pointer event
    // that carries no modifier flags of its own (see _select).
    private _modifierActive = false;
    private _keyHandler: ((e: KeyboardEvent) => void) | null = null;
    private _blurHandler: (() => void) | null = null;

    constructor(scene: Scene, traceScene: TraceScene) {
        this.scene = scene;
        this.traceScene = traceScene;
    }

    /** Track ctrl/meta/shift via DOM keyboard events, for pointer events without flags. */
    private _attachModifierTracking(): void {
        if (this._keyHandler) return;
        this._keyHandler = (e: KeyboardEvent) => {
            this._modifierActive = e.ctrlKey || e.metaKey || e.shiftKey;
        };
        // A blur can strip key state without a keyup; reset to avoid sticky modifiers.
        this._blurHandler = () => { this._modifierActive = false; };
        // keydown + keyup both update from the event's current modifier flags.
        window.addEventListener('keydown', this._keyHandler);
        window.addEventListener('keyup', this._keyHandler);
        window.addEventListener('blur', this._blurHandler);
    }

    /** Remove the DOM modifier listeners (mirrors _attachModifierTracking). */
    private _detachModifierTracking(): void {
        if (this._keyHandler) {
            window.removeEventListener('keydown', this._keyHandler);
            window.removeEventListener('keyup', this._keyHandler);
            this._keyHandler = null;
        }
        if (this._blurHandler) {
            window.removeEventListener('blur', this._blurHandler);
            this._blurHandler = null;
        }
        this._modifierActive = false;
    }

    /** Enable or disable click-to-select pointer handling */
    public enableClickToSelect(enabled: boolean, options?: ClickToSelectOptions): void {
        if (options) {
            this.options = { ...options };
            this.sample = null;
            this._resetCycle();
        }
        this.clickToSelectEnabled = enabled;

        if (enabled && this.scene) {
            // Only add pointer observable if we haven't already added it to this scene
            if (!this.pointerObserver) {
                this.pointerObserver = this.scene.onPointerObservable.add((pointerInfo) => {
                    if (!this._cycling()) this._handlePointer(pointerInfo);
                });
                Logger.log("[ClickToSelect] Added pointer observable to scene");
            }
            this._attachModifierTracking();

            Logger.log("[ClickToSelect] Click-to-select enabled");
        } else if (!enabled) {
            // Stop tracking modifiers while selection is off (re-attached on
            // re-enable). Avoids leaking window listeners when the editor tears
            // down or switches to play mode.
            this._detachModifierTracking();
        }
        this._configureCycleObservers();
    }

    private _cycling(): boolean {
        return this.clickToSelectEnabled && this.options.cycle === true && !this.traceScene.isPlayMode();
    }

    private _handlePointer(pointerInfo: PointerInfo): void {
        if (pointerInfo.type === PointerEventTypes.POINTERDOWN) this._handlePointerDown(pointerInfo);
        else if (pointerInfo.type === PointerEventTypes.POINTERUP) this._handlePointerUp(pointerInfo);
        else if (pointerInfo.type === PointerEventTypes.POINTERMOVE) this._handlePointerMove(pointerInfo);
    }

    private _configureCycleObservers(): void {
        this.scene.onPrePointerObservable.remove(this.prePointerObserver);
        this.prePointerObserver = null;
        this.traceScene.getEventSystem?.()?.removeEventListener('trace-viewer:selection-changed', this.selectionChanged);
        if (this.clickToSelectEnabled && this.options.cycle) {
            // Utility layers can swallow ordinary pointer events. Observe their
            // pre-events without changing skip flags or taking their capture.
            this.prePointerObserver = this.scene.onPrePointerObservable.add(info => {
                if (this._cycling()) this._handlePointer(new PointerInfo(info.type, info.event, info.originalPickingInfo));
            });
            this.traceScene.getEventSystem?.()?.addEventListener('trace-viewer:selection-changed', this.selectionChanged);
        } else {
            this.sample = null;
            this._resetCycle();
        }
    }

    private _resetCycle(): void {
        this.cycle = null;
        this.lastClick = null;
    }

    /**
     * The view a ray is cast in: the picking camera, its pose, its lens and the
     * viewport. Ray hits and a cycle hold for one view. It is compared by value:
     * a render target or a second active camera swaps the scene's camera, and
     * recomputes its projection, every frame, so change notifications fire on
     * frames where nothing the user sees has moved. The near and far planes
     * are left out: they change how far a ray reaches, not where it points.
     */
    private _viewOf(camera: Camera | null): string {
        if (!camera) return '';
        const engine = this.scene.getEngine();
        // The getters recompute a dirty matrix, so a camera moved since the last render reads its new pose.
        // The projection carries framing's lens shift (zoomInto in core/Camera.ts).
        const pose = camera.getViewMatrix().m;
        const lens = camera.getProjectionMatrix().m;
        const { x, y, width, height } = camera.viewport;
        return [camera.uniqueId, camera.mode, camera.fov, camera.orthoLeft, camera.orthoRight, camera.orthoTop, camera.orthoBottom,
            x, y, width, height, engine.getRenderWidth(), engine.getRenderHeight(), ...pose, ...lens].join(',');
    }

    private _selectionSnapshot(): readonly (SceneObject | null)[] {
        return [this.traceScene.getSelectedObject(), ...(this.options.getSelection?.() ?? [])];
    }

    private _checkSelection(): void {
        const selection = this._selectionSnapshot();
        if (selection.length !== this.selection.length || selection.some((entry, i) => entry !== this.selection[i])) {
            this._resetCycle();
            this.selection = selection;
            this.candidates = null;
        }
    }

    private _movedFromCycle(x: number, y: number): boolean {
        return !!this.cycle && (x - this.cycle.x) ** 2 + (y - this.cycle.y) ** 2 >= ClickToSelect.CYCLE_THRESHOLD_SQUARED;
    }

    private _pickTargets(x: number, y: number): Candidates {
        const camera = this.scene.cameraToUseForPointers ?? this.scene.activeCamera;
        const view = this._viewOf(camera);
        this._checkSelection();
        if (this._movedFromCycle(x, y) || (this.cycle && this.cycle.view !== view)) this._resetCycle();
        if (!this.sample || this.sample.x !== x || this.sample.y !== y || this.sample.view !== view) {
            // Eligibility is pickability, not naming: a mesh under an object
            // selects it even when named like a helper, as a plain click does.
            // A meshless behavior's marker (`__audioGizmo`) and a saved Shape's
            // `__builtin_<type>__` mesh are picked that way. A `__collider` picker
            // can still be pickable in edit mode right after a load, so it is
            // filtered here.
            const hits = camera ? this.scene.multiPick(x, y, mesh =>
                isPickableMesh(mesh) && !mesh.metadata?.editorHandle &&
                !mesh.name.endsWith(COLLIDER_MESH_SUFFIX) &&
                !this.options.isGizmoMesh?.(mesh), camera) ?? [] : [];
            this.sample = { x, y, view, hits: hits.filter(hit => hit.hit && hit.pickedMesh).sort((a, b) => a.distance - b.distance) };
            this.candidates = null;
        }
        if (this.candidates) return this.candidates;
        const targets: SceneObject[] = [];
        const seen = new Set<SceneObject>();
        const shown = new Map<AbstractMesh, SceneObject | null>();
        let hasContent = false;
        for (const hit of this.sample.hits) {
            const raw = this._findSceneObjectForMesh(hit.pickedMesh!);
            if (!raw) continue;
            hasContent = true;
            const target = this.options.resolveSelectionUnit ? this.options.resolveSelectionUnit(raw, hit) : raw;
            shown.set(hit.pickedMesh!, target);
            if (target && !seen.has(target)) {
                seen.add(target);
                targets.push(target);
            }
        }
        if (this.cycle && (targets.length !== this.cycle.targets.length || targets.some((target, i) => target !== this.cycle!.targets[i]))) {
            // Host mapping can change after our first select (e.g. entering a
            // selection unit). It must not turn the second tap into a new click.
            // The nearest hit that showed the selection and now shows something
            // else is where the host stepped in, so that is next; failing that,
            // the cycle goes past the selection while it is still a target.
            const selected = this.traceScene.getSelectedObject();
            const before = this.cycle.shown;
            const stepped = selected
                ? [...shown].find(([mesh, target]) => target && target !== selected && before.get(mesh) === selected)?.[1] ?? null
                : null;
            const kept = selected ? targets.indexOf(selected) : -1;
            const next = stepped ? targets.indexOf(stepped) : kept >= 0 ? (kept + 1) % targets.length : -1;
            this.cycle = next >= 0 ? { ...this.cycle, nextIndex: next, targets, shown } : null;
        }
        return this.candidates = { targets, hasContent, shown, view };
    }

    /** Preview without advancing. Coordinates are canvas-relative CSS pixels. */
    public peekNextPick(x = this.scene.pointerX, y = this.scene.pointerY): SceneObject | null {
        if (!this._cycling()) return null;
        const { targets } = this._pickTargets(x, y);
        return targets[this.cycle?.nextIndex ?? 0] ?? null;
    }

    private _selectNext(pointerInfo: PointerInfo): void {
        const x = this.scene.pointerX;
        const y = this.scene.pointerY;
        // A click must see scene edits made while the hover ray was cached.
        this.sample = null;
        const { targets, hasContent, shown, view } = this._pickTargets(x, y);
        if (this.lastClick && this.pointerDownTime - this.lastClick.time < Scene.DoubleClickDelay &&
            (x - this.lastClick.x) ** 2 + (y - this.lastClick.y) ** 2 < ClickToSelect.CYCLE_THRESHOLD_SQUARED) {
            this.lastClick = null;
            return;
        }
        this.lastClick = { time: this.pointerDownTime, x, y };
        const index = this.cycle?.nextIndex ?? 0;
        const target = targets[index];
        if (target) this.cycle = { x, y, nextIndex: (index + 1) % targets.length, targets, shown, view };
        this.selecting = true;
        try {
            if (target) this._select(target, pointerInfo);
            else if (!hasContent) this.traceScene.handlePointerMiss();
        } finally {
            this.selecting = false;
            this.selection = this._selectionSnapshot();
            this.candidates = null;
        }
    }

    private _select(sceneObject: SceneObject, pointerInfo: PointerInfo): void {
        const event = pointerInfo.event;
        // A modifier the gesture reports is right. Babylon passes the DOM pointer event,
        // which reports all three, so false there is a plain click. The keyboard latch
        // stands in only for an event without flags, because a keyup it misses would
        // otherwise leave every later plain click additive (#1148).
        const additive = event.ctrlKey || event.metaKey || event.shiftKey
            || (typeof event.ctrlKey !== 'boolean' && this._modifierActive);
        this.traceScene.handlePointerEvent('select', sceneObject, true, { additive });
    }

    /**
     * Release pointer, selection and window modifier listeners.
     */
    public dispose(): void {
        this.clickToSelectEnabled = false;
        this._detachModifierTracking();
        this._configureCycleObservers();
        this.scene.onPointerObservable.remove(this.pointerObserver);
        this.pointerObserver = null;
    }

    /** Object that received the pointer-down :active event, for release cleanup */
    private _activeObject: SceneObject | null = null;

    /**
     * Mark the current pointer interaction as a drag. The next POINTERUP
     * will skip the select fire. Used by XR 6DoF to prevent hand-grab
     * releases from being interpreted as clicks (screen-space deltas are
     * near zero for controller grabs, defeating the 5px threshold).
     */
    public notifyDragOccurred(): void {
        this._dragOccurred = true;
        this._resetCycle();
    }

    private _handlePointerDown(pointerInfo: PointerInfo): void {
        if (!this.clickToSelectEnabled) return;

        // Only handle left-click — right-click is reserved for camera panning
        const event = pointerInfo.event;
        if (event.button !== 0) return;

        this.pointerDownPosition = { x: event.clientX, y: event.clientY };
        this.pointerDownPickInfo = pointerInfo;
        this.pointerDownTime = Date.now();
        // Reset per-interaction drag flag so a stale signal from a prior
        // aborted interaction (e.g. XR session exit mid-grab with no
        // POINTERUP) can't swallow this click.
        this._dragOccurred = false;

        // Fire :active (pointer-down) — CSS :active semantics
        const pickResult = pointerInfo.pickInfo;
        if (this._cycling() || (pickResult && pickResult.hit && pickResult.pickedMesh)) {
            const sceneObject = this._cycling() ? this.peekNextPick() : this._findSceneObjectForMesh(pickResult!.pickedMesh!);
            if (sceneObject) {
                this.traceScene.handlePointerEvent('active', sceneObject, true);
                this._activeObject = sceneObject;
            }
        }
    }

    private _handlePointerUp(pointerInfo: PointerInfo): void {
        if (!this.clickToSelectEnabled) return;

        const event = pointerInfo.event;
        if (event.button !== 0) return;

        // Release :active state
        if (this._activeObject) {
            this.traceScene.handlePointerEvent('active', this._activeObject, false);
            this._activeObject = null;
        }

        if (!this.pointerDownPosition || !this.pointerDownPickInfo) return;

        const dx = event.clientX - this.pointerDownPosition.x;
        const dy = event.clientY - this.pointerDownPosition.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        const pickInfo = this.pointerDownPickInfo;
        this.pointerDownPosition = null;
        this.pointerDownPickInfo = null;

        if (distance > ClickToSelect.CLICK_THRESHOLD) {
            Logger.log(`[ClickToSelect] Drag detected (${distance.toFixed(1)}px) - ignoring`);
            this._dragOccurred = false;
            return;
        }

        // External drag signal (XR 6DoF, etc.) takes precedence over
        // the screen-space threshold. Consume it so the next real click fires.
        if (this._dragOccurred) {
            this._dragOccurred = false;
            Logger.log('[ClickToSelect] Drag signalled externally - suppressing select');
            return;
        }

        if (this._cycling()) {
            this._selectNext(pointerInfo);
            return;
        }

        const pickResult = pickInfo.pickInfo;

        if (pickResult && pickResult.hit && pickResult.pickedMesh) {
            const sceneObject = this._findSceneObjectForMesh(pickResult.pickedMesh);
            Logger.log(`[ClickToSelect] Click on mesh "${pickResult.pickedMesh.name}" → SceneObject "${sceneObject?.name ?? '(none)'}"`);

            if (sceneObject) {
                // Ctrl/Cmd or Shift held → additive selection (edit mode only).
                // The runtime still tracks a single primary; the additive hint is
                // forwarded so an editor multi-selection model can toggle membership.
                this._select(sceneObject, pointerInfo);
            }
        } else {
            Logger.log(`[ClickToSelect] Click on empty space (no hit)`);
            this.traceScene.handlePointerMiss();
        }
    }

    private _handlePointerMove(pointerInfo: PointerInfo): void {
        if (!this.clickToSelectEnabled) return;

        if (this._cycling()) {
            if (this.pointerDownPosition &&
                Math.hypot(pointerInfo.event.clientX - this.pointerDownPosition.x, pointerInfo.event.clientY - this.pointerDownPosition.y) > ClickToSelect.CLICK_THRESHOLD) {
                this.notifyDragOccurred();
            }
            this._updateHover(this.peekNextPick());
            return;
        }

        // Babylon populates pickInfo on POINTERMOVE when meshes have enablePointerMoveEvents=true
        // (set in _updateMeshPickability for pseudo-state objects).
        // Fallback to explicit pick if pickInfo is missing (shouldn't happen in normal flow).
        let pickResult = pointerInfo.pickInfo;
        if (!pickResult || !pickResult.hit) {
            pickResult = this.scene.pick(
                this.scene.pointerX,
                this.scene.pointerY,
                isPickableMesh,
            );
        }

        let newHoveredObject: SceneObject | null = null;

        if (pickResult && pickResult.hit && pickResult.pickedMesh) {
            newHoveredObject = this._findSceneObjectForMesh(pickResult.pickedMesh);
        }

        this._updateHover(newHoveredObject);
    }

    private _updateHover(newHoveredObject: SceneObject | null): void {
        if (newHoveredObject !== this.hoveredObject) {
            if (this.hoveredObject) {
                this.traceScene.handlePointerEvent('hover', this.hoveredObject, false);
            }
            if (newHoveredObject) {
                this.traceScene.handlePointerEvent('hover', newHoveredObject, true);
            }
            this.hoveredObject = newHoveredObject;
        }
    }

    /** Walk up from a picked mesh to find its owning SceneObject */
    private _findSceneObjectForMesh(mesh: AbstractMesh): SceneObject | null {
        const sceneObjects = this.traceScene.getSceneObjects();

        const rootToObject = new Map<TransformNode, SceneObject>();
        for (const sceneObject of sceneObjects) {
            rootToObject.set(sceneObject.transform, sceneObject);
        }

        let current: TransformNode | null = mesh;
        while (current) {
            const sceneObject = rootToObject.get(current);
            if (sceneObject !== undefined) {
                return sceneObject;
            }
            current = current.parent as TransformNode | null;
        }

        return null;
    }

}
